import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Download, ClipboardList } from 'lucide-react';
import { adminUserOrders, adminSymbols } from '@/api/admin';
import type { Money, UserOrder, UserOrderStatus } from '@/api/types';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Pill } from '@/components/widgets/Pill';
import { ApiError } from '@/lib/http';
import { downloadCsv } from '@/lib/csv';
import { fmtDateTime, fmtMoney, fmtNum, moneyToNumber } from '@/lib/format';

const TIME_RANGES = [
  { label: '今日', hours: 24 },
  { label: '近 7 天', hours: 24 * 7 },
  { label: '近 30 天', hours: 24 * 30 },
  { label: '全部', hours: 0 },
];

const SIDE_FILTERS: { label: string; value: 'ALL' | 'BUY' | 'SELL' }[] = [
  { label: '全部', value: 'ALL' },
  { label: '买入 BUY', value: 'BUY' },
  { label: '卖出 SELL', value: 'SELL' },
];

const STATUS_FILTERS: { label: string; value: 'ALL' | 'OPEN' | 'SETTLING' | 'CLOSED' }[] = [
  { label: '全部', value: 'ALL' },
  { label: '持仓中', value: 'OPEN' },
  { label: '结算中', value: 'SETTLING' },
  { label: '已平', value: 'CLOSED' },
];

const STATUS_LABEL: Record<UserOrderStatus, string> = {
  FILLED: '持仓中',
  PARTIAL: '部分平仓',
  SETTLING: '结算中',
  CLOSED: '已平',
};

const STATUS_TONE: Record<UserOrderStatus, 'up' | 'warn' | 'neutral'> = {
  FILLED: 'up',
  PARTIAL: 'warn',
  SETTLING: 'warn',
  CLOSED: 'neutral',
};

const PAGE_SIZE = 50;

/**
 * 用户订单 — App 用户(虚拟账户)逐笔下单 / 持仓 / 已平。
 *
 * 数据源 GET /admin/api/v1/user-orders(virtual_legs,用户视角)。区别于「真实成交明细」(真实 hedging 账户台账):
 * 这里每行是某 App 用户自己的订单。持仓中(FILLED/PARTIAL)显示后端按当前行情派生的实时浮动盈亏(毛价差,
 * 与 App 资产页同口径),已平(CLOSED)显示平仓价 + 已扣服务费的净盈亏。支持用户 / 状态 / 方向 / 品种 / 时间筛选 + 分页。
 */
export function UserOrders() {
  // 支持从「真实成交明细」成交详情下钻带 userId 进来(?userId=…)初始过滤。
  const [searchParams] = useSearchParams();
  const [hours, setHours] = useState<number>(24);
  const [side, setSide] = useState<'ALL' | 'BUY' | 'SELL'>('ALL');
  const [status, setStatus] = useState<'ALL' | 'OPEN' | 'SETTLING' | 'CLOSED'>('ALL');
  const [userId, setUserId] = useState(() => searchParams.get('userId') ?? '');
  const [symbolId, setSymbolId] = useState<string>('ALL');
  const [page, setPage] = useState(1);

  const fromIso = useMemo(
    () => (hours > 0 ? new Date(Date.now() - hours * 3600_000).toISOString() : undefined),
    [hours],
  );

  const symbolsQ = useQuery({ queryKey: ['admin', 'symbols'], queryFn: adminSymbols.list });
  const symbolName = (id: string) =>
    symbolsQ.data?.find((s) => s.symbolId === id)?.symbolName ?? id;

  const trimmedUser = userId.trim();

  // 任一筛选变更回到第 1 页。
  const setHoursP = (v: number) => { setHours(v); setPage(1); };
  const setSideP = (v: typeof side) => { setSide(v); setPage(1); };
  const setStatusP = (v: typeof status) => { setStatus(v); setPage(1); };
  const setUserP = (v: string) => { setUserId(v); setPage(1); };
  const setSymbolP = (v: string) => { setSymbolId(v); setPage(1); };

  const listQ = useQuery({
    queryKey: ['admin', 'user-orders', hours, side, status, trimmedUser, symbolId, page],
    queryFn: () =>
      adminUserOrders.list({
        page,
        pageSize: PAGE_SIZE,
        ...(fromIso ? { from: fromIso } : {}),
        ...(side !== 'ALL' ? { side } : {}),
        ...(status !== 'ALL' ? { status } : {}),
        ...(trimmedUser ? { userId: trimmedUser } : {}),
        ...(symbolId !== 'ALL' ? { symbolId } : {}),
      }),
    placeholderData: (prev) => prev,
    refetchInterval: 10_000, // 含实时浮动,刷新比真实台账(30s)更勤
  });

  const items = listQ.data?.items ?? [];
  const total = listQ.data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div className="p-6 space-y-4">
      <header className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-xl font-bold text-ink-0 flex items-center gap-2">
            <ClipboardList className="h-5 w-5 text-brand" />
            用户订单
          </h1>
          <p className="text-sm text-ink-2 mt-1">
            App 用户(虚拟账户)逐笔订单 · 持仓中实时浮动盈亏 / 已平显示平仓价与净盈亏(区别于「真实成交明细」真实台账)
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          disabled={items.length === 0}
          title={`仅导出当前页 ${items.length} 条(每页 ${PAGE_SIZE} 条);翻页后再次导出可获取其余数据`}
          onClick={() =>
            downloadCsv('user-orders', items, [
              { key: 'openedAt', header: '下单时间', format: (v) => fmtDateTime(v as string) },
              { key: 'userId', header: '用户ID' },
              { key: 'symbolId', header: '品种', format: (v) => symbolName(String(v)) },
              { key: 'side', header: '方向' },
              { key: 'status', header: '状态', format: (v) => STATUS_LABEL[v as UserOrderStatus] ?? String(v) },
              { key: 'volume', header: '手数', format: (v) => (Number(v) / 100).toFixed(2) },
              { key: 'entryPrice', header: '开仓价', format: (v) => fmtNum(Number(v), 2) },
              { key: 'closePrice', header: '平仓价', format: (v) => (v == null ? '' : fmtNum(Number(v), 2)) },
              { key: 'markPrice', header: '当前价', format: (v) => (v == null ? '' : fmtNum(Number(v), 2)) },
              { key: 'realizedPnl', header: '已实现净盈亏', format: (v) => csvMoney(v as Money | null) },
              { key: 'floatingPnl', header: '浮动盈亏', format: (v) => csvMoney(v as Money | null) },
              { key: 'serviceFee', header: '服务费', format: (v) => csvMoney(v as Money | null) },
              { key: 'legId', header: '订单ID' },
            ])
          }
        >
          <Download className="h-4 w-4" />
          导出当前页 CSV
        </Button>
      </header>

      {/* Filter chips */}
      <div className="flex flex-col gap-2">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs text-ink-3 mr-1">时间</span>
          {TIME_RANGES.map((t) => (
            <Button key={t.label} variant={hours === t.hours ? 'default' : 'outline'} size="sm" onClick={() => setHoursP(t.hours)}>
              {t.label}
            </Button>
          ))}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs text-ink-3 mr-1">状态</span>
          {STATUS_FILTERS.map((f) => (
            <Button key={f.value} variant={status === f.value ? 'default' : 'outline'} size="sm" onClick={() => setStatusP(f.value)}>
              {f.label}
            </Button>
          ))}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs text-ink-3 mr-1">方向</span>
          {SIDE_FILTERS.map((f) => (
            <Button key={f.value} variant={side === f.value ? 'default' : 'outline'} size="sm" onClick={() => setSideP(f.value)}>
              {f.label}
            </Button>
          ))}
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <span className="text-xs text-ink-3 mr-1">用户ID</span>
            <Input
              value={userId}
              onChange={(e) => setUserP(e.target.value)}
              placeholder="用户ID(回车筛选)"
              className="h-8 w-40"
            />
            {trimmedUser && (
              <Button variant="ghost" size="sm" onClick={() => setUserP('')}>清除</Button>
            )}
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs text-ink-3 mr-1">品种</span>
            <select
              value={symbolId}
              onChange={(e) => setSymbolP(e.target.value)}
              className="h-8 rounded-md border border-line bg-bg-3 px-2 text-sm text-ink-1"
            >
              <option value="ALL">全部</option>
              {(symbolsQ.data ?? []).map((s) => (
                <option key={s.symbolId} value={s.symbolId}>{s.symbolName}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>
            订单 · {listQ.data ? total : '—'} 条
            {total > 0 && (
              <span className="ml-2 text-xs text-ink-3 font-normal num">第 {page}/{totalPages} 页</span>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {listQ.isError && (
            <div className="px-4 py-3 text-sm text-down-dim">
              无法加载:{listQ.error instanceof ApiError ? listQ.error.message : '未知错误'}
            </div>
          )}
          <div className="overflow-x-auto">
            <table className="w-full text-sm whitespace-nowrap">
              <thead className="bg-bg-3 text-ink-2 text-xs">
                <tr>
                  <th className="px-3 py-2 text-left font-normal">下单时间</th>
                  <th className="px-3 py-2 text-left font-normal">用户ID</th>
                  <th className="px-3 py-2 text-left font-normal">品种</th>
                  <th className="px-3 py-2 text-left font-normal">方向</th>
                  <th className="px-3 py-2 text-left font-normal">状态</th>
                  <th className="px-3 py-2 text-right font-normal">手数</th>
                  <th className="px-3 py-2 text-right font-normal">开仓价</th>
                  <th className="px-3 py-2 text-right font-normal">平仓价/现价</th>
                  <th className="px-3 py-2 text-right font-normal" title="持仓中为浮动盈亏;已平为已扣服务费的净盈亏">浮动/净盈亏</th>
                </tr>
              </thead>
              <tbody>
                {items.map((o) => (
                  <UserOrderRow key={o.legId} o={o} symbolName={symbolName(o.symbolId)} />
                ))}
                {items.length === 0 && !listQ.isLoading && (
                  <tr>
                    <td colSpan={9} className="px-3 py-8 text-center text-ink-3">没有符合条件的订单</td>
                  </tr>
                )}
                {listQ.isLoading && (
                  <tr>
                    <td colSpan={9} className="px-3 py-8 text-center text-ink-3">加载中...</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          {total > PAGE_SIZE && (
            <div className="flex items-center justify-end gap-2 px-4 py-3 border-t border-line">
              <Button variant="outline" size="sm" disabled={page <= 1 || listQ.isFetching} onClick={() => setPage((p) => Math.max(1, p - 1))}>
                上一页
              </Button>
              <span className="text-xs text-ink-3 num">{page} / {totalPages}</span>
              <Button variant="outline" size="sm" disabled={page >= totalPages || listQ.isFetching} onClick={() => setPage((p) => Math.min(totalPages, p + 1))}>
                下一页
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function UserOrderRow({ o, symbolName }: { o: UserOrder; symbolName: string }) {
  const isOpen = o.status === 'FILLED' || o.status === 'PARTIAL';
  return (
    <tr className="border-t border-line row-hover">
      <td className="px-3 py-1.5 num text-ink-2">{fmtDateTime(o.openedAt)}</td>
      <td className="px-3 py-1.5 num text-ink-1">{o.userId}</td>
      <td className="px-3 py-1.5 text-ink-1">{symbolName}</td>
      <td className="px-3 py-1.5">
        <Pill tone={o.side === 'BUY' ? 'up' : 'down'}>{o.side}</Pill>
      </td>
      <td className="px-3 py-1.5">
        <Pill tone={STATUS_TONE[o.status]}>{STATUS_LABEL[o.status]}</Pill>
      </td>
      <td className="px-3 py-1.5 num text-right text-ink-1">{(o.volume / 100).toFixed(2)}</td>
      <td className="px-3 py-1.5 num text-right text-ink-1">{fmtNum(o.entryPrice, 2)}</td>
      <td className="px-3 py-1.5 num text-right">
        <MarkCell o={o} />
      </td>
      <td className="px-3 py-1.5 num text-right">
        <PnlCell o={o} isOpen={isOpen} />
      </td>
    </tr>
  );
}

/** 平仓价(CLOSED)或当前价(持仓中);行情滞后/缺失弱化展示。 */
function MarkCell({ o }: { o: UserOrder }) {
  if (o.status === 'SETTLING') {
    return <span className="text-warn">结算中</span>;
  }
  if (o.status === 'CLOSED') {
    return o.closePrice != null
      ? <span className="text-ink-1">{fmtNum(o.closePrice, 2)}</span>
      : <span className="text-ink-3">—</span>;
  }
  if (o.markPrice == null) return <span className="text-ink-3" title="行情缺失">—</span>;
  return (
    <span className={o.markStale ? 'text-ink-3' : 'text-ink-1'} title={o.markStale ? '行情滞后,浮动盈亏可能不准' : undefined}>
      {fmtNum(o.markPrice, 2)}
      {o.markStale && <span className="ml-1 text-[10px]">滞后</span>}
    </span>
  );
}

/** 盈亏:持仓中显示浮动盈亏,已平显示已扣服务费的净盈亏;按正负染色,附小标签。 */
function PnlCell({ o, isOpen }: { o: UserOrder; isOpen: boolean }) {
  if (o.status === 'SETTLING') {
    return <span className="text-warn">等待落定</span>;
  }
  const m = isOpen ? o.floatingPnl : o.realizedPnl;
  if (!m || m.value == null) return <span className="text-ink-3">—</span>;
  const n = moneyToNumber(m);
  const tone = n > 0 ? 'text-up' : n < 0 ? 'text-down' : 'text-ink-1';
  return (
    <span className={tone}>
      {fmtMoney(m, { sign: true })}
      <span className="ml-1 text-[10px] text-ink-3">{isOpen ? '浮动' : '净'}</span>
    </span>
  );
}

/** CSV 导出用:Money → 纯数字字符串(无符号前缀,Excel 友好) */
function csvMoney(m: Money | null | undefined): string {
  if (!m || m.value == null) return '';
  return moneyToNumber(m).toFixed(2);
}
